import type { NotificationGlyphName } from "@/lib/notificationPresentation";

type NotificationGlyphIconProps = {
  name: NotificationGlyphName;
  size?: number;
  className?: string;
};

/**
 * NotificationGlyphIcon renders the outline icon for a notification glyph using
 * inline SVG (no emoji), matching the Ionicons outlines used by the mobile app.
 */
export function NotificationGlyphIcon({ name, size = 20, className }: NotificationGlyphIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {GLYPH_PATHS[name]}
    </svg>
  );
}

const GLYPH_PATHS: Record<NotificationGlyphName, React.ReactNode> = {
  ticket: (
    <>
      <path d="M4 8.5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2V10a2 2 0 0 0 0 4v1.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V14a2 2 0 0 0 0-4z" />
      <path d="M14 6.5v11" strokeDasharray="2 2" />
    </>
  ),
  trophy: (
    <>
      <path d="M8 4h8v5a4 4 0 0 1-8 0z" />
      <path d="M8 6H5.5a1 1 0 0 0-1 1 3 3 0 0 0 3 3" />
      <path d="M16 6h2.5a1 1 0 0 1 1 1 3 3 0 0 1-3 3" />
      <path d="M12 13v3" />
      <path d="M8.5 20h7M10 16h4v4" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <circle cx="16.5" cy="9" r="2.5" />
      <path d="M15.5 14.5a4.5 4.5 0 0 1 5 4.5" />
    </>
  ),
  "person-add": (
    <>
      <circle cx="10" cy="8" r="3.5" />
      <path d="M3.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M19 8v6M16 11h6" />
    </>
  ),
  person: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </>
  ),
  cash: (
    <>
      <rect x="3" y="7" width="18" height="11" rx="2" />
      <circle cx="12" cy="12.5" r="2.5" />
      <path d="M6.5 10h.01M17.5 15h.01" />
    </>
  ),
  "paper-plane": (
    <>
      <path d="M21 3 3 10.5l7.5 2.5L13 21z" />
      <path d="m10.5 13 10.5-10" />
    </>
  ),
  pricetag: (
    <>
      <path d="M3 12.5V5a2 2 0 0 1 2-2h7.5L21 11.5 11.5 21z" />
      <circle cx="8" cy="8" r="1.4" />
    </>
  ),
  time: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  bell: (
    <>
      <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </>
  ),
};
